// src/app.js

import { PublicClientApplication } from "@azure/msal-browser";

const STORAGE_ACCOUNT = process.env.PARCEL_STORAGE_ACCOUNT;
const MSAL_CLIENT_ID = process.env.PARCEL_MSAL_CLIENT_ID;

const msalConfig = {
  auth: {
    clientId: MSAL_CLIENT_ID,
    authority: "https://login.microsoftonline.com/e81c845a-b877-44c3-bc58-ef6e9173838f",
    redirectUri: window.location.origin
  },
  cache: {
    cacheLocation: "sessionStorage"
  }
};

const msalInstance = new PublicClientApplication(msalConfig);
const loginRequest = {
  scopes: ["https://storage.azure.com/user_impersonation"]
};

let accessToken = null;

/**
 * Downloads the specified blob from the given container.
 */
async function downloadBlob(containerName, blobName) {
  const out = document.getElementById("output");
  const blobUrl = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${containerName}/${blobName}`;

  out.textContent = `⏳ Downloading ${blobName}…`;
  try {
    const resp = await fetch(blobUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "x-ms-version": "2020-10-02"
      }
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = blobName;
    document.body.appendChild(a);
    a.click();
    a.remove();

    out.textContent = `✅ Download of ${blobName} started`;
  } catch (e) {
    console.error("Download failed:", e);
    out.textContent = `❌ Download error: ${e.message}`;
  }
}

/**
 * Main flow: initialize MSAL, handle redirect, acquire token, download blob.
 */
async function run() {
  const out = document.getElementById("output");

  // 1) Initialize the MSAL instance
  out.textContent = "Initializing MSAL…";
  await msalInstance.initialize();

  // 2) Process a redirect response if present
  out.textContent = "Handling redirect…";
  const redirectResp = await msalInstance.handleRedirectPromise();
  if (redirectResp) {
    msalInstance.setActiveAccount(redirectResp.account);

    // Restore the original URL (remove code & state)
    const original = decodeURIComponent(redirectResp.state || "");
    const cleanUrl = window.location.origin + original;
    window.history.replaceState({}, document.title, cleanUrl);

    out.textContent = "Logged in via redirect.";
  }

  // 3) If not signed in yet, redirect to Azure AD and pass original path as "state"
  const account = msalInstance.getActiveAccount();
  if (!account) {
    const originalPath = window.location.pathname + window.location.search;
    out.textContent = "Redirecting to sign-in…";
    return msalInstance.loginRedirect({
      ...loginRequest,
      state: encodeURIComponent(originalPath)
    });
  }

  // 4) Parse container & blob names from the (restored) URL
  const params = new URLSearchParams(window.location.search);
  const containerName = params.get("container");
  const blobName = params.get("blob");
  if (!containerName || !blobName) {
    out.textContent = "❌ Missing ?container=…&blob=… in URL";
    return;
  }

  // 5) Acquire an access token silently (fallback to redirect if needed)
  out.textContent = "Acquiring token silently…";
  try {
    const silentResp = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account
    });
    accessToken = silentResp.accessToken;
  } catch {
    out.textContent = "Redirecting for token…";
    return msalInstance.acquireTokenRedirect(loginRequest);
  }

  // 6) Download the blob now that we have a token
  out.textContent = `✅ Token acquired; downloading ${blobName}…`;
  await downloadBlob(containerName, blobName);
}

// Kick off the workflow
run();
